import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart,
  Home,
  Inventory,
  ShoppingCartOutlined,
  Assignment,
  Receipt,
  Search,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { useApiCart } from '../../hooks/useApiCartProvider';
import { SUPPORTED_LANGUAGES, ROUTES } from '../../config/constants';
import NotificationBell from '../notifications/NotificationBell';

const NAV_ITEMS = [
  { labelKey: 'navigation.dashboard', icon: <Home fontSize="small" />, path: ROUTES.DASHBOARD },
  { labelKey: 'navigation.products', icon: <Inventory fontSize="small" />, path: ROUTES.PRODUCTS },
  { labelKey: 'navigation.cart', icon: <ShoppingCartOutlined fontSize="small" />, path: ROUTES.CART },
  { labelKey: 'navigation.orders', icon: <Assignment fontSize="small" />, path: ROUTES.ORDERS },
  { labelKey: 'navigation.invoices', icon: <Receipt fontSize="small" />, path: ROUTES.INVOICES },
];

const CustomerHeader: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartItemCount } = useCart();
  const { totalItems } = useApiCart();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [userMenuEl, setUserMenuEl] = React.useState<null | HTMLElement>(null);

  const handleLogout = () => {
    setUserMenuEl(null);
    logout();
    navigate(ROUTES.LOGIN);
  };

  const handleLanguageToggle = () => {
    const next = i18n.language === SUPPORTED_LANGUAGES.ID ? SUPPORTED_LANGUAGES.EN : SUPPORTED_LANGUAGES.ID;
    i18n.changeLanguage(next);
    setUserMenuEl(null);
  };

  const cartCount = totalItems || getCartItemCount();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 0.5, md: 1 } }}>
      {/* Logo */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { xs: 1, md: 2 }, flexShrink: 0 }}
        onClick={() => navigate(ROUTES.DASHBOARD)}
      >
        <img
          src="/Mowilex-Corporate.png"
          alt="Mowilex Logo"
          style={{ height: 36, objectFit: 'contain' }}
        />
      </Box>

      {/* Nav links — desktop only */}
      {isDesktop && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Button
                key={item.path}
                size="small"
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  textTransform: 'none',
                  color: isActive ? 'error.main' : 'text.secondary',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.8rem',
                  px: 1.5,
                  whiteSpace: 'nowrap',
                  '&:hover': { color: 'error.main', bgcolor: 'transparent' },
                }}
              >
                {t(item.labelKey)}
              </Button>
            );
          })}
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Search — desktop only */}
      {isDesktop && (
        <TextField
          size="small"
          placeholder={t('products.searchPlaceholder')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const val = (e.target as HTMLInputElement).value;
              if (val) navigate(`/products?search=${encodeURIComponent(val)}`);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 180,
            maxWidth: 220,
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'grey.50' },
            '& .MuiOutlinedInput-input': { py: 0.8, fontSize: '0.85rem' },
          }}
        />
      )}

      {/* Notification Bell */}
      <NotificationBell />

      {/* Cart */}
      <IconButton
        color="inherit"
        aria-label="shopping cart"
        onClick={() => navigate(ROUTES.CART)}
        size="small"
      >
        <Badge badgeContent={cartCount} color="error" max={99}>
          <ShoppingCart fontSize="small" />
        </Badge>
      </IconButton>

      {/* User avatar + dropdown */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          ml: 0.5,
          borderRadius: 2,
          px: 1,
          py: 0.5,
          '&:hover': { bgcolor: 'grey.50' },
        }}
        onClick={(e) => setUserMenuEl(e.currentTarget)}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'error.main',
            fontSize: '0.85rem',
            fontWeight: 700,
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        {isDesktop && (
          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 120 }}>
            {user?.name}
          </Typography>
        )}
        <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary' }} />
      </Box>

      {/* User menu */}
      <Menu
        anchorEl={userMenuEl}
        open={Boolean(userMenuEl)}
        onClose={() => setUserMenuEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>{user?.full_name || user?.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { setUserMenuEl(null); navigate(ROUTES.PROFILE); }}>
          {t('navigation.profile')}
        </MenuItem>
        <MenuItem onClick={handleLanguageToggle}>
          {i18n.language === 'id' ? '🇬🇧 English' : '🇮🇩 Bahasa Indonesia'}
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          {t('navigation.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CustomerHeader;
